// eslint-disable-next-line no-unused-vars
import React from 'react';
import { func, bool, string } from 'prop-types';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { Button, Loading } from '@carbon/react';
import { RefreshCw, Package, Gift, Boxes, Trash } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';

import { CartItemPropType } from 'Types/cart';

import Img from 'Containers/Image/Image';
import QuantitySelect from 'Containers/QuantitySelector/QuantitySelect';
import Price from 'Components/Price/Price';
import { StyledOverlay } from 'Components/Products/Products';
import useNumberUtils from 'Hooks/useNumberUtils';

import { PROMOTION_PRGF } from 'Constants';

const StyledProductCartItem = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: calc(100% - 30px);
  margin: 0 auto;
  padding: 10px 0;
  font-size: 14px;

  &.lowStock {
    padding-bottom: 20px;
  }

  &:not(:last-of-type) {
    border-bottom: 1px solid ${ props => props.theme.products.colors.border };
  }

  .overlay {
    align-items: center;
    justify-content: center;
    cursor: not-allowed;
    background-color: rgba(255, 255, 255, 0.65);

    p {
      color: ${ props => props.theme.colors.default };
      font-size: 1rem;
      font-weight: 600;
    }
  }

  .cds--loading-overlay {
    margin-left: -1rem;
    width: calc(100% + 2rem);
    position: absolute;
    z-index: 99;

    svg {
      transform: scale(1.75);
    }
  }

  .preview {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3px;
    min-width: 50px;
    height: 50px;
    width: 50px;
    margin-right: 7px;
    box-sizing: border-box;
    border: 1px solid ${ props => props.theme.colors.shadowGray };

    a {
      display: block;
      height: 100%;
      width: 100%;
    }

    .giftIcon {
      position: absolute;
      left: -8px;
      top: -8px;
      transform: scale(0.75);
      z-index: 1;
    }

    svg.image {
      max-width: 70%;
      stroke: #AFAEAE;
    }

    .image {
      position: relative;
      top: 50%;
      transform: translateY(-50%);
      display: block;
      margin: 0 auto;
      height: auto;
      width: auto;
      max-height: 100%;
      max-width: 100%;
    }
  }

  .title, .sku {
    font-size: 0.875rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .title {
    width: 100%;
    max-width: 170px;
    margin-right: 10px;
    color: ${ props => props.theme.colors.darkGray };

    a {
      &:hover {
        color: ${ props => props.theme.colors.blue };
      }
      color: ${ props => props.theme.colors.darkGray };
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  .sku {
    width: 100%;
    max-width: 130px;
    font-weight: 600;
    margin-right: 10px;
    color: ${ props => props.theme.colors.darkGray };

    span {
      color: ${ props => props.theme.colors.gray };
      font-weight: normal;
    }
  }

  .quantityContainer {
    margin-right: 10px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    position: relative;

    .lowStock {
      position: absolute;
      bottom: -22px;
      left: 50%;
      transform: translateX(-50%);
      white-space: nowrap;
      font-size: 0.825rem;
      color: ${ props => props.theme.colors.darkGray };
      font-weight: 600;
    }
  }

  .minQuantity {
    line-height: 1rem;
    font-size: 0.875rem;
    font-weight: normal;
    color: ${ props => props.theme.colors.darkGray };
    white-space: nowrap;

    svg {
      position: relative;
      top: 3px;
      margin-right: 7px;
      transform: scale(0.9);
    }
  }

  .quantityExceeded {
    position: relative;
    top: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;

    p {
      font-size: 0.875rem;
      color: ${ props => props.theme.colors.default };
      white-space: nowrap;
    }
  }

  .quantitySelector {
    button {
      width: 22.5px;
    }

    input {
      width: 30px;
      font-size: 0.875rem;
    }
  }

  .addToCart {
    svg {
      margin-right: 5px;
    }

    height: 30px;
    min-height: auto;
    padding: 0 1em;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${ props => props.theme.colors.default };
  }

  .refreshPrice {
    font-size: 0.875rem;
    color: ${ props => props.theme.colors.default };
    padding: 0;
    margin-left: 5px;
    height: 15px;
    width: 15px;
    min-height: 0;
    justify-content: center;
    align-content: center;

    &.refreshing {
      @keyframes refreshing {
        from {
          transform: rotate(0);
        }

        to {
          transform: rotate(1080deg);
        }
      }

      svg {
        animation: refreshing 3s;
        animation-iteration-count: infinite;
      }
    }

    svg {
      width: 15px;
      height: 15px;
      margin: 0 5px;
    }
  }

  .price {
    min-width: 70px;
    margin-right: 13px;
    text-align: right;
    text-transform: capitalize;

    .currentPrice {
      &:not(.free) {
        color: ${ props => props.theme.colors.darkGray };
      }

      font-size: 0.875rem;
      font-weight: 600;
    }

    .oldPrice {
      color: ${ props => props.theme.colors.gray };
      font-size: 0.75rem;
    }
  }

  .deleteContainer {
    width: 26px;
  }

  .delete {
    &:hover {
      background-color: #eadedc;
    }

    width: 26px;
    padding: 0;
    height: 25px;
    min-height: 0;
    min-width: 0;
    border: 1px solid #FEE7E5;
    background-color: #FCF3F1;
    color: ${ props => props.theme.colors.darkGray };
    outline: 0;
    box-shadow: none;
    text-align: center;
    z-index: 13;

    svg {
      margin: 0 auto;
      width: 12px;
      height: 13px;
    }
  }
`;

const StyledCartPromotion = styled.div`
  position: relative;
  top: -1px;
  padding: 0;
  background-color: ${ props => props.theme.products.colors.promotionsRibbon };
  border-top: 1px solid ${ props => props.theme.colors.lightBlue };
  border-bottom: 1px solid ${ props => props.theme.colors.lightBlue };

  .promoHeading {
    position: relative;
    height: 40px;
    padding: 0 15px;
    margin-bottom: -5px;
    background-color: #E7ECF5;
    display: flex;
    align-items: center;
    font-size: 1rem;
    color: ${ props => props.theme.colors.darkGray };
    z-index: 50;

    svg {
      margin-right: 10px;
      color: inherit;
    }
  }

  .cartItem {
    padding: 16px 0;

    .preview {
      background-color: ${ props => props.theme.colors.white };
      border: 1px solid ${ props => props.theme.products.colors.border };
    }

    &:not(:last-of-type) {
      border: 0;

      &.lowStock:after {
        top: 80%;
      }

      &:after {
        display: block;
        position: absolute;
        left: 16px;
        top: 84%;
        content: '+';
        font-size: 25px;
        font-weight: 600;
        color: ${ props => props.theme.products.colors.inStock };
        z-index: 13;
      }
    }

    :last-of-type {
      border-bottom: none;
    }
  }
`;

const ProductCartItem = (
  {
    loading,
    product: prod,
    onDelete,
    onChange,
    onQuantityRefresh,
    isRefreshing,
    defaultCurrency,
    isExternalReseller
  }
) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { formatNumber } = useNumberUtils();
  const { promotion } = prod;

  const getProduct = product => (
    (
      <StyledProductCartItem
        key={ product.itemId || product.id || product.productId }
        theme={ theme }
        className={
          `cartItem ${ product.isPromo ? 'promoItem' : '' } ${ product.stockValue < 10 ? 'lowStock' : '' }`
        }
      >
        { loading && <Loading active={ loading } small withOverlay /> }
        <StyledOverlay className="overlay" open={ product.minQuantity > product.stockValue }>
          <p>{ t('products.product') } { t('products.outOfStock') } !</p>
        </StyledOverlay>
        <div className="preview">
          { promotion?.type === PROMOTION_PRGF && product.isPromo && (
            <Gift className="giftIcon" />
          ) }
          <Link
            to={ `/products/${ product.slug }/pd/${ product.productId || product.id }` }
            data-test="cartItemNavigation"
            data-testid={ product.productId || product.id }
            state={ {
              component: 'cart',
              source: 'checkout_page',
              clickedOn: product.isPromo ? 'promo_image' : 'image',
              productId: product.productId || product.id
            } }
          >
            <Img
              src={ product.pictureUrl }
              className="image"
              unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
              loading="lazy"
              loader={ <Loading active={ loading } small withOverlay={ false } /> }
              alt={ product.name || product.title }
            />
          </Link>
        </div>
        <h4 className="title">
          <Link
            to={ `/products/${ product.slug }/pd/${ product.productId || product.id }` }
            data-test="cartItemNavigation"
            data-testid={ product.productId || product.id }
            state={ {
              component: 'cart',
              source: 'cart_title',
              clickedOn: product.isPromo ? 'promo_title' : 'title',
              productId: product.productId || product.id
            } }
          >
            { product.title }
          </Link>
        </h4>
        <p className="sku">{ t('product.code') }: <span>{ product.code }</span></p>
        <div className="quantityContainer">
          {/*{ (product.minQuantity > 1 && product.stockValue >= product.minQuantity) && (*/ }
          {/*  <div>*/ }
          {/*    <p className="minQuantity">*/ }
          {/*      <SquareStack />*/ }
          {/*      { t('products.multipleOf', { multiple: product.minQuantity }) }*/ }
          {/*    </p>*/ }
          {/*  </div>*/ }
          {/*) }*/ }
          <QuantitySelect
            size="sm"
            value={ Math.max(product.quantity, product.minQuantity || 1) }
            min={ Math.max(product.minQuantity, 1) }
            step={ Math.max(product.minQuantity, 1) }
            max={ product.stockValue }
            onChange={ onChange }
            disabled={ !product.mainProduct }
            data-test="cartItemChangeQty"
            data-testid={ product.productId || product.id }
          />
          { (!product.isPromo && product.quantity > product.stockValue) && (
            <div className="quantityExceeded">
              <p>{ t('products.stock') }: { product.stockValue }</p>
              <Button
                className={ `refreshPrice ${ isRefreshing ? 'refreshing' : '' }` }
                kind="ghost"
                renderIcon={props => <RefreshCw size={32} {...props} />}
                iconDescription={ t('product.refreshQty') }
                onClick={ onQuantityRefresh }
                data-test="cartItemRefreshQty"
                data-testid={ product.productId || product.id }
              />
            </div>
          ) }
          { (!product.isPromo && product.stockValue < 10) && (
            <p className="lowStock">{ t('products.lowStock', { stock: product.stockValue }) }</p>
          ) }
        </div>
        { !Object.keys(product?.warnings || {}).length && (
          <Price
            price={ product.quantity * isExternalReseller ? product.price : product.ronPrice }
            promoPrice={ product.quantity * isExternalReseller ? product.promoPrice : product.ronPromoPrice }
            currency={ defaultCurrency }
            shouldContainTooltip
            withTooltip={ !product.isPromo }
            tooltipContent={ `
              ${ t('products.greenStamp') }
              ${ formatNumber(product.greenStampValue, { currency: defaultCurrency }) }
          ` }
          />
        ) }
        <div className="deleteContainer">
          { !product.isPromo && (
            <Button
              className="delete"
              kind="ghost"
              renderIcon={ Trash }
              iconDescription={ t('common.delete') }
              onClick={ onDelete }
              data-test="cartItemDelete"
              data-testid={ product.productId || product.id }
            />
          ) }
        </div>
      </StyledProductCartItem>
    )
  );

  if (
    prod.promoItems
    && prod.promoItems[promotion?.promotionId]
  ) {
    return (
      <StyledCartPromotion
        key={ `${ prod.itemId || prod.id || prod.productId }_${ promotion.promotionId }` }
        theme={ theme }
      >
        <div className="promoHeading">
          <Boxes />
          <p
            dangerouslySetInnerHTML={ {
              __html: t(`products.promotionData.type.${ promotion.type }`)
            } }
          />
        </div>
        { getProduct(prod) }
        { prod.promoItems[promotion.promotionId].map(p => getProduct({ ...p, isPromo: true })) }
      </StyledCartPromotion>
    );
  }

  return getProduct(prod);
};

ProductCartItem.propTypes = {
  product: CartItemPropType,
  loading: bool,
  onQuantityRefresh: func,
  isRefreshing: bool,
  onChange: func,
  onDelete: func,
  defaultCurrency: string,
  isExternalReseller: bool
};

ProductCartItem.displayName = 'ProductCartItemComponent';

export default ProductCartItem;
